import { Controller } from '@hotwired/stimulus'

/**
 * A generic form validation controller that enables/disables submit buttons
 * based on whether all required fields have values.
 *
 * Usage:
 * <form data-controller="validate-form">
 *   <input required data-validate-form-target="input" data-action="change->validate-form#validate">
 *   <select required data-validate-form-target="input" data-action="change->validate-form#validate">
 *     <!-- options -->
 *   </select>
 *   <button type="submit" data-validate-form-target="submit">Submit</button>
 * </form>
 */
export class ValidateFormController extends Controller {
  static targets = ['input', 'submit']

  declare readonly inputTargets: HTMLInputElement[]
  declare readonly submitTarget: HTMLInputElement
  declare readonly hasSubmitTarget: boolean

  connect() {
    // Initial validation
    this.validate()
  }

  validate() {
    const isValid = this.isFormValid()

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !isValid
    }

    // Dispatch a custom event that other controllers can listen for
    this.element.dispatchEvent(
      new CustomEvent('form-validation', {
        bubbles: true,
        detail: { isValid },
      }),
    )

    return isValid
  }

  /**
   * Check if all required inputs have values
   */
  private isFormValid(): boolean {
    if (this.inputTargets.length == 0) {
      return false
    }

    return this.inputTargets.every((input) => {
      // Skip inputs that aren't required
      if (!input.required) {
        return true
      }

      // For file inputs, check if files are selected
      if (input.type === 'file') {
        return input.dataset.filePersisted
          ? true
          : input.files && input.files.length > 0
      }

      // For select elements
      if (input.tagName.toLowerCase() === 'select') {
        return input.value !== ''
      }

      // For checkboxes and radio buttons
      if (input.type === 'checkbox' || input.type === 'radio') {
        return input.checked
      }

      // For all other inputs, check if they have a value
      return input.value.trim() !== ''
    })
  }

  /**
   * Reset the form validation state
   */
  reset() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
    }
  }

  submitForm(event) {
    event.preventDefault()

    if (this.validate()) {
      ;(this.element as HTMLFormElement).submit()
    }
  }
}
